import { useEffect, useState } from 'react';
import Button from '../../../../reusable/Button/Button';
import StatusChip from '../../../../reusable/StatusChip/StatusChip';
import ApplicationDialog from './ApplicationDialog';
import PromoCodeSetupDialog, { ClaimedCode } from './PromoCodeSetupDialog';
import { APPLICATION_STATUS, CODE_LABELS, codeKeysFor, codeValue, promoCodeEntry, rulesFor } from './promoCodes';
import { formatDate } from '../format';
import { t } from '../../../../i18n/i18n';
import useAffiliateAccess from '../../useAffiliateAccess';

const normalise = (status) => String(status || '').trim().toLowerCase();

/** "Your Super Affiliate Promo Codes" — the codes an approved affiliate claimed. */
function PromoCodeList({ superAffiliate }) {
  const claimed = codeKeysFor(superAffiliate)
    .map((key) => ({ key, entry: promoCodeEntry(superAffiliate, key) }))
    .filter(({ entry }) => codeValue(entry));

  // Super Affiliates made before the application flow keep admin-created codes
  // and claimed none, so there is nothing to show them.
  if (!claimed.length) return null;

  return (
    <section className="sa-promo">
      <h3 className="sa-promo__title">{t('Your Super Affiliate Promo Codes')}</h3>
      {claimed.map(({ key, entry }) => (
        <ClaimedCode key={key} label={t(CODE_LABELS[key]) || key} entry={entry} rules={rulesFor(superAffiliate, key)} />
      ))}
    </section>
  );
}

/**
 * Super Affiliate — live's card, apply flow and promo-code setup
 * (`SuperAffiliateSection.jsx` on main), driven by the dashboard's
 * `super_affiliate` block and `is_super_affiliate`.
 */
export default function SuperAffiliate({ superAffiliate, isSuperAffiliate }) {
  const [applyOpen, setApplyOpen] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  // Set on submit, so the status flips before the dashboard refetch lands.
  const [justApplied, setJustApplied] = useState(false);
  // A manual dismissal sticks, so the pop-up doesn't fight the user.
  const [promoDismissed, setPromoDismissed] = useState(false);

  const status = normalise(superAffiliate?.application_status);
  const approved = Boolean(isSuperAffiliate) || status === APPLICATION_STATUS.approved;
  const underReview = !approved && (justApplied || status === APPLICATION_STATUS.underReview);
  const rejected = !approved && !underReview && status === APPLICATION_STATUS.rejected;
  // A retainer who isn't a Super Affiliate can't apply (`/affiliate/user/status/`).
  const { canApply: statusAllowsApply } = useAffiliateAccess();
  const canApply = statusAllowsApply && Boolean(superAffiliate?.can_apply) && !underReview;

  // Only affiliates approved through the application are prompted; legacy
  // Super Affiliates keep their admin-created codes.
  const setupRequired =
    approved && Boolean(superAffiliate?.can_setup_promo_codes) && Boolean(superAffiliate?.promo_setup_required);

  // Open while `promo_setup_required` holds. Claiming both codes flips it on
  // the next dashboard fetch, which closes this for good.
  useEffect(() => {
    if (setupRequired && !promoDismissed) setPromoOpen(true);
    if (!setupRequired) setPromoOpen(false);
  }, [setupRequired, promoDismissed]);

  const statusLine = () => {
    if (approved) return t('You are a Super Affiliate.');
    if (!statusAllowsApply) return t('Retainer affiliates can’t apply to become a Super Affiliate.');
    if (underReview) return t('Your application has been sent and is under review.');
    if (rejected) {
      const reapplyOn = superAffiliate?.can_reapply_on;
      const cooldown = superAffiliate?.reapply_cooldown_days;
      if (reapplyOn) return t('Your application was rejected. You can apply again from {{date}}.', { date: formatDate(reapplyOn) });
      return cooldown
        ? t('Your application was rejected. You can apply again after {{count}} days.', { count: cooldown })
        : t('Your application was rejected.');
    }
    return t('Apply to unlock Super Affiliate commission and promo codes.');
  };

  // Approved has its own badge (below); the chip is for the dark card's states.
  const chip = underReview ? 'Under Review' : rejected ? 'Rejected' : null;

  return (
    <>
      <section className={`sa-card${approved ? ' is-active' : ''}`}>
        <img className="sa-card__star" src="/images/super-affiliate-star.svg" alt="" width={124} height={41} />

        <div className="sa-card__copy">
          <div className="sa-card__head">
            <h2 className="sa-card__title">Super Affiliate</h2>
            {approved && (
              // The dark-green success chip sank into the blue card, so the
              // approved card wears a badge made for it: frosted white with a
              // solid green tick.
              <span className="sa-badge">
                <span className="sa-badge__tick" aria-hidden="true">
                  <span className="icon sa-badge__tick-icon" style={{ '--icon': 'url(/icons/check-sm.svg)' }} />
                </span>
                {t('Approved')}
              </span>
            )}
            {chip && (
              <StatusChip tone={underReview ? 'warning' : undefined} size="sm">
                {t(chip)}
              </StatusChip>
            )}
          </div>
          <p className="sa-card__status">{statusLine()}</p>
        </div>

        <div className="sa-card__actions">
          {!approved && statusAllowsApply && (
            <Button
              variant="submit"
              height={40}
              paddingInline={22}
              disabled={!canApply}
              onClick={() => setApplyOpen(true)}
            >
              {underReview ? t('Application Submitted') : t('Apply Here')}
            </Button>
          )}
          {/* Reopens the pop-up after a dismissal, until both codes are claimed. */}
          {setupRequired && !promoOpen && (
            <Button
              variant="light"
              height={40}
              paddingInline={22}
              onClick={() => {
                setPromoDismissed(false);
                setPromoOpen(true);
              }}
            >
              {t('Set Up Promo Codes')}
            </Button>
          )}
        </div>
      </section>

      {approved && <PromoCodeList superAffiliate={superAffiliate} />}

      <ApplicationDialog
        open={applyOpen}
        onClose={() => setApplyOpen(false)}
        onSubmitted={() => setJustApplied(true)}
      />

      <PromoCodeSetupDialog
        open={promoOpen}
        superAffiliate={superAffiliate}
        onClose={() => {
          setPromoDismissed(true);
          setPromoOpen(false);
        }}
      />
    </>
  );
}
